import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOpportunity,
  parseOpportunityUrlPreview,
} from "@/api/opportunities";
import { listDealPipelines } from "@/api/deal-pipelines";
import { isValidJobUrl } from "@/lib/resolve-opportunity-url";

export function OpportunitiesAddPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  const [dealName, setDealName] = useState(searchParams.get("role") ?? "");
  const [company, setCompany] = useState(searchParams.get("company") ?? "");
  const [pipelineConfigId, setPipelineConfigId] = useState("");
  const [stageId, setStageId] = useState("");
  const [amount, setAmount] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState(searchParams.get("url") ?? "");
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const pipelinesQuery = useQuery({
    queryKey: ["deal-pipelines"],
    queryFn: listDealPipelines,
    staleTime: 60_000,
  });

  const pipelines = pipelinesQuery.data?.pipelines ?? [];
  const selectedPipeline =
    pipelines.find((p) => p.pipelineConfigId === pipelineConfigId) ??
    pipelines.find((p) => p.isDefault) ??
    pipelines[0];

  useEffect(() => {
    if (!selectedPipeline) return;
    if (!pipelineConfigId) {
      setPipelineConfigId(selectedPipeline.pipelineConfigId);
    }
    if (!stageId && selectedPipeline.stages[0]) {
      setStageId(selectedPipeline.stages[0].id);
    }
  }, [selectedPipeline, pipelineConfigId, stageId]);

  useEffect(() => {
    if (!selectedPipeline) return;
    const stillValid = selectedPipeline.stages.some((s) => s.id === stageId);
    if (!stillValid && selectedPipeline.stages[0]) {
      setStageId(selectedPipeline.stages[0].id);
    }
  }, [selectedPipeline, stageId]);

  const createMutation = useMutation({
    mutationFn: () => {
      const amountNum = amount.trim() ? Number(amount) : undefined;
      const stage = selectedPipeline?.stages.find((s) => s.id === stageId);
      return createOpportunity({
        company: company.trim(),
        dealName: dealName.trim(),
        pipelineConfigId: selectedPipeline?.pipelineConfigId,
        stageId: stage?.id,
        stage: stage?.name,
        amount: Number.isFinite(amountNum) ? amountNum : undefined,
        closeDate: closeDate || undefined,
        ownerName: ownerName.trim() || undefined,
        source: "manual",
        ...(importUrl.trim() ? { url: importUrl.trim() } : {}),
      });
    },
    onSuccess: (opp) => {
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      navigate(`/deals/${opp.id}`);
    },
    onError: (err: unknown) => {
      setErrorMsg(err instanceof Error ? err.message : "Create failed");
    },
  });

  const importMutation = useMutation({
    mutationFn: () => parseOpportunityUrlPreview(importUrl, "ai"),
    onSuccess: (result) => {
      if (result.success) {
        if (result.fields.company) setCompany(result.fields.company);
        if (result.fields.dealName) setDealName(result.fields.dealName);
        if (result.fields.estimatedValue) {
          const n = Number(
            String(result.fields.estimatedValue).replace(/[^0-9.]/g, ""),
          );
          if (Number.isFinite(n)) setAmount(String(n));
        }
        setImportMsg("Fields filled from link. Review and create the deal.");
      } else {
        setImportMsg(result.error ?? "Could not parse URL");
      }
    },
    onError: (err: unknown) => {
      setImportMsg(err instanceof Error ? err.message : "Parse failed");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!company.trim() || !dealName.trim()) {
      setErrorMsg("Deal name and account (company) are required.");
      return;
    }
    setErrorMsg("");
    createMutation.mutate();
  };

  return (
    <div className="mx-auto max-w-xl">
      <header className="mb-8">
        <Link
          to="/deals"
          className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          ← Back to Deals
        </Link>
        <h1 className="text-2xl font-bold text-foreground">Create deal</h1>
        <p className="text-muted-foreground">
          Name the deal, pick an account and stage — same primary flow as Apollo.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block text-sm">
          Deal name *
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
            value={dealName}
            onChange={(e) => setDealName(e.target.value)}
            required
            autoFocus
          />
        </label>

        <label className="block text-sm">
          Account (company) *
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            required
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            Pipeline
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2"
              value={selectedPipeline?.pipelineConfigId ?? ""}
              onChange={(e) => {
                setPipelineConfigId(e.target.value);
                setStageId("");
              }}
            >
              {pipelines.map((p) => (
                <option key={p.pipelineConfigId} value={p.pipelineConfigId}>
                  {p.name}
                  {p.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            Stage
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2"
              value={stageId}
              onChange={(e) => setStageId(e.target.value)}
            >
              {(selectedPipeline?.stages ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            Amount
            <input
              type="number"
              min={0}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Close date
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
            />
          </label>
        </div>

        <label className="block text-sm">
          Owner
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Display name"
          />
        </label>

        {errorMsg ? (
          <p className="text-sm text-red-600">{errorMsg}</p>
        ) : null}

        <button
          type="submit"
          disabled={createMutation.isPending}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {createMutation.isPending ? "Creating…" : "Create deal"}
        </button>
      </form>

      <div className="mt-10 border-t border-border pt-6">
        <button
          type="button"
          onClick={() => setImportOpen((o) => !o)}
          className="text-sm font-medium text-brand hover:underline"
        >
          {importOpen ? "Hide" : "Import from link"}
        </button>
        {importOpen ? (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Optional — paste a signal or listing URL to prefill fields.
            </p>
            <input
              type="url"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://…"
            />
            <button
              type="button"
              disabled={!isValidJobUrl(importUrl) || importMutation.isPending}
              onClick={() => importMutation.mutate()}
              className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              {importMutation.isPending ? "Parsing…" : "Fill from URL"}
            </button>
            {importMsg ? (
              <p className="text-xs text-muted-foreground">{importMsg}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
